/** A drawing that came alive: a convex-hull rigid body carrying the original strokes. */

import type { Stroke } from './stroke.ts';
import type { Vec } from './vec.ts';
import { boundingRadius, convexHull, polygonCentroid, polygonInertia, regularPolygon } from './geom.ts';
import { sub } from './vec.ts';

/** How a shape behaves once it is alive. Each one must visibly change the sim. */
export type Behavior = 'normal' | 'bouncy' | 'balloon' | 'spinner' | 'heavy';

export interface BehaviorTraits {
  /** Multiplier on world gravity. Negative floats upward. */
  gravity: number;
  restitution: number;
  friction: number;
  /** Per-second velocity retention (air drag). 1 = no drag. */
  damping: number;
  angularDamping: number;
  /** Density multiplier — heavier shapes shove lighter ones around. */
  density: number;
  /** Constant angular velocity applied every step (rad/s), 0 for none. */
  spin: number;
}

export const BEHAVIORS: Record<Behavior, BehaviorTraits> = {
  normal: { gravity: 1, restitution: 0.28, friction: 0.35, damping: 0.999, angularDamping: 0.995, density: 1, spin: 0 },
  bouncy: { gravity: 1, restitution: 0.86, friction: 0.12, damping: 1, angularDamping: 0.999, density: 0.7, spin: 0 },
  balloon: { gravity: -0.32, restitution: 0.5, friction: 0.05, damping: 0.985, angularDamping: 0.97, density: 0.35, spin: 0 },
  spinner: { gravity: 0.55, restitution: 0.45, friction: 0.5, damping: 0.998, angularDamping: 1, density: 1, spin: 7 },
  heavy: { gravity: 1.6, restitution: 0.03, friction: 0.7, damping: 0.995, angularDamping: 0.94, density: 3.2, spin: 0 },
};

export const BEHAVIOR_ORDER: Behavior[] = ['normal', 'bouncy', 'balloon', 'spinner', 'heavy'];

export interface Body {
  id: number;
  /** Convex collider in local space, centroid at the origin. */
  verts: Vec[];
  /** The child's actual drawing, in the same local space, for rendering. */
  strokes: Stroke[];
  pos: Vec;
  angle: number;
  vel: Vec;
  angVel: number;
  invMass: number;
  invInertia: number;
  radius: number;
  behavior: Behavior;
  /** Squash-and-stretch amount, decays to 0. Purely cosmetic. */
  wobble: number;
  /** Seconds this body has been nearly still — used to park it and save battery. */
  restTime: number;
  /** Position at the end of the previous step, for the stillness test. */
  restX: number;
  restY: number;
  restAngle: number;
  asleep: boolean;
  /** True while a finger is dragging it. */
  held: boolean;
  /** Set on impact so the renderer can flash and the mixer can play a sound. */
  lastImpact: number;
}

let nextId = 1;

/** Reset the id counter — tests only, so ids are deterministic per run. */
export function resetBodyIds(): void {
  nextId = 1;
}

export interface BodyOptions {
  behavior?: Behavior;
  vel?: Vec;
  angVel?: number;
  angle?: number;
  /** Set to make the body immovable (the ground, a wall, a jar). */
  static?: boolean;
}

/**
 * Build a body from world-space points (a drawn outline). The hull is
 * recentred on its centroid so rotation happens about the visual middle.
 */
export function createBody(worldPoints: Vec[], strokes: Stroke[], opts: BodyOptions = {}): Body {
  const hull = convexHull(worldPoints);
  const usable = hull.length >= 3;
  const centre = polygonCentroid(usable ? hull : worldPoints);
  // A single dot has no hull — give it a small round body so it still plays.
  const verts = usable ? hull.map((p) => sub(p, centre)) : regularPolygon(8, 14);

  const traits = BEHAVIORS[opts.behavior ?? 'normal'];
  const areaMass = Math.max(polygonAreaSafe(verts), 300) * 0.0016 * traits.density;
  const inertia = polygonInertia(verts, { x: 0, y: 0 }) * areaMass;

  return {
    id: nextId++,
    verts,
    strokes: strokes.map((s) => ({ ...s, points: s.points.map((p) => sub(p, centre)) })),
    pos: { ...centre },
    angle: opts.angle ?? 0,
    vel: opts.vel ? { ...opts.vel } : { x: 0, y: 0 },
    angVel: opts.angVel ?? 0,
    invMass: opts.static ? 0 : 1 / areaMass,
    invInertia: opts.static ? 0 : 1 / Math.max(inertia, 1e-3),
    radius: boundingRadius(verts, { x: 0, y: 0 }),
    behavior: opts.behavior ?? 'normal',
    wobble: 0,
    restTime: 0,
    restX: centre.x,
    restY: centre.y,
    restAngle: opts.angle ?? 0,
    asleep: false,
    held: false,
    lastImpact: 0,
  };
}

function polygonAreaSafe(verts: Vec[]): number {
  let a = 0;
  for (let i = 0; i < verts.length; i++) {
    const p = verts[i];
    const q = verts[(i + 1) % verts.length];
    a += p.x * q.y - p.y * q.x;
  }
  return Math.abs(a) / 2;
}

/** Swap a body's behavior, rescaling its mass so the change is felt immediately. */
export function setBehavior(body: Body, behavior: Behavior): void {
  if (body.invMass === 0) return;
  const oldDensity = BEHAVIORS[body.behavior].density;
  const newDensity = BEHAVIORS[behavior].density;
  const ratio = oldDensity / newDensity;
  body.invMass *= ratio;
  body.invInertia *= ratio;
  body.behavior = behavior;
  body.asleep = false;
  body.restTime = 0;
}

/** Impulse at a world point — used by taps, throws and collisions. */
export function applyImpulse(body: Body, impulse: Vec, contact: Vec): void {
  if (body.invMass === 0) return;
  body.vel.x += impulse.x * body.invMass;
  body.vel.y += impulse.y * body.invMass;
  const rx = contact.x - body.pos.x;
  const ry = contact.y - body.pos.y;
  body.angVel += (rx * impulse.y - ry * impulse.x) * body.invInertia;
  body.asleep = false;
  body.restTime = 0;
}
